import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useRootStore } from '../../../../../providers/root-store-provider';
import { Text } from '../../../../app/text/text';
import { StyledLink } from '../../../../app/styled-link/styled-link';

interface DialogAccountLinksPropsInterface {
    compact?: boolean;
    type?:
        | 'login'
        | 'forgottenPassword'
        | 'signUp'
        | 'emailConfirmation'
        | 'resetPassword'
        | 'partnerSignUp'
        | 'alreadyLoggedIn'
        | null;
}

const AccountLink: React.FC<{ children: React.ReactNode; onClick: () => void }> = ({ children, onClick }) => (
    <button
        type="button"
        data-auth-navigation
        onClick={onClick}
        className="cursor-pointer border-0 bg-transparent p-0 font-normal text-web-copy underline decoration-[1px] decoration-[#9b8d82]/60 underline-offset-[3px] transition-colors hover:text-[#4f433b] hover:decoration-[#72645a] focus-visible:text-[#4f433b] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-web-copy"
    >
        {children}
    </button>
);

export const DialogAccountLinks: React.FC<DialogAccountLinksPropsInterface> = observer(
    ({ compact = false, type = null }) => {
        const { t } = useTranslation();
        const rootStore = useRootStore();
        const { authDialogStore } = rootStore.authStore;

        const showForgottenPassword =
            (type === 'login' || type === 'signUp') && (!compact || authDialogStore.authMethod === 'password');

        if (compact) {
            return (
                <div className="flex flex-col items-center justify-between gap-vsmall text-center sm:flex-row [&>p:only-child]:mx-auto">
                    {showForgottenPassword && (
                        <Text size="small" align="center">
                            <AccountLink onClick={() => authDialogStore.setForgottenPassActive()}>
                                {t('dialogs.common.forgottenPassword')}
                            </AccountLink>
                        </Text>
                    )}
                    {(type === 'resetPassword' || type === 'forgottenPassword') && (
                        <Text size="small" align="center">
                            {t('dialogs.common.alreadyHaveAccount')}{' '}
                            <AccountLink onClick={() => authDialogStore.setLoginActive()}>
                                {t('dialogs.common.logIn')}
                            </AccountLink>
                        </Text>
                    )}
                    {(type === 'login' || type === 'resetPassword' || type === 'forgottenPassword') && (
                        <Text size="small" align="center">
                            {t('dialogs.common.dontHaveAccount')}{' '}
                            <AccountLink onClick={() => authDialogStore.setSignupActive()}>
                                {t('dialogs.common.signUp')}
                            </AccountLink>
                        </Text>
                    )}
                    {type === 'signUp' && (
                        <Text size="small" align="center">
                            {t('dialogs.common.alreadyHaveAccount')}{' '}
                            <AccountLink onClick={() => authDialogStore.setLoginActive()}>
                                {t('dialogs.common.logIn')}
                            </AccountLink>
                        </Text>
                    )}
                </div>
            );
        }

        return (
            <div className="flex flex-col items-center">
                <Text gap="xsmall" size="large" align="left" semiBold>
                    {showForgottenPassword && (
                        <StyledLink onClick={() => authDialogStore.setForgottenPassActive()}>
                            {t('dialogs.common.forgottenPassword')}
                        </StyledLink>
                    )}
                    {(type === 'resetPassword' || type === 'forgottenPassword') && (
                        <>
                            {t('dialogs.common.alreadyHaveAccount')}{' '}
                            <StyledLink onClick={() => authDialogStore.setLoginActive()}>
                                {t('dialogs.common.logIn')}
                            </StyledLink>
                        </>
                    )}
                </Text>
                <Text gap="xsmall" size="large" align="right" semiBold>
                    {(type === 'login' || type === 'resetPassword' || type === 'forgottenPassword') && (
                        <>
                            {t('dialogs.common.dontHaveAccount')}{' '}
                            <StyledLink onClick={() => authDialogStore.setSignupActive()}>
                                {t('dialogs.common.signUp')}
                            </StyledLink>
                        </>
                    )}
                    {type === 'signUp' && (
                        <>
                            {t('dialogs.common.alreadyHaveAccount')}{' '}
                            <StyledLink onClick={() => authDialogStore.setLoginActive()}>
                                {t('dialogs.common.logIn')}
                            </StyledLink>
                        </>
                    )}
                </Text>
            </div>
        );
    },
);
